<template>
  <FieldFrame v-bind="frameBindings">
    <template
      v-for="slotName in forwardedFieldSlots()"
      :key="slotName"
      #[slotName]
    >
      <slot :name="slotName" />
    </template>

    <template #default="control">
      <UPinInput
        ref="pinInput"
        v-model="code"
        :name="name"
        v-bind="{ ...groupAccessibility, ...fieldControlAria(control) }"
        :length="length"
        :disabled="disabled"
        :autofocus="autofocus"
        :type="pinType"
        otp
        size="md"
        variant="none"
        fixed
        :ui="controlUi"
        @complete="onComplete"
      />
    </template>
  </FieldFrame>
</template>

<script setup lang="ts">
import { computed, toRefs, useTemplateRef } from 'vue'
import UPinInput from '@nuxt/ui/components/PinInput.vue'

import FieldFrame from './FieldFrame/FieldFrame.molecule.vue'
import { useFieldFrame } from './FieldFrame/useFieldFrame'
import { fieldControlAria } from '../base/fieldControlAria'
import { fieldControlUi } from '../base/fieldControlUi'
import { useFieldPresentation } from '~/design-system/molecules/useFieldPresentation'
import type { FieldPresentationProps } from '~/interfaces/FieldPresentationProps'
import type { FieldHelpSlots } from '~/interfaces/FieldHelpSlots'
import { fieldPresentationDefaults } from '~/design-system/molecules/fieldPresentationDefaults'
import { omitEmptyStringProps } from '../base/omitEmptyStringProps'

const props = withDefaults(
  defineProps<
    FieldPresentationProps & {
      /** Controlled verification-code value as a plain string. */
      modelValue?: string
      /** Number of code slots. */
      length?: number
      /** Accepted characters: digits only, or letters and digits. */
      mode?: 'alphanumeric' | 'numeric'
      /** Focuses the first slot on mount. */
      autofocus?: boolean
    }
  >(),
  {
    ...fieldPresentationDefaults,
    autofocus: false,
    length: 6,
    mode: 'numeric',
    modelValue: '',
    name: 'code'
  }
)
const emit = defineEmits<{
  'update:modelValue': [value: string]
  /** Fires once when every slot is filled. */
  complete: [value: string]
}>()

defineSlots<FieldHelpSlots>()

const { error, help, hint, label, name, required, serverError } = toRefs(props)
const { message, controlUi } = useFieldPresentation(
  { error, help, serverError },
  fieldControlUi.pinInput
)
const { frameBindings, forwardedFieldSlots } = useFieldFrame(
  { help, hint, label, name, required },
  message,
  ['help']
)

// UPinInput spends the field id on its aria-hidden mirror input, so the group
// carries the name. See "Let the field own the control id" in docs/design-system-planes.md.
const groupAccessibility = computed(() =>
  omitEmptyStringProps({ 'aria-label': props.label, role: 'group' })
)

const pinType = computed(() => (props.mode === 'numeric' ? 'number' : 'text'))

const ACCEPTED_CHARACTERS = { alphanumeric: /[^a-zA-Z0-9]/gu, numeric: /\D/gu } as const

/** Vendor pin input binds an array of per-slot characters; this molecule exposes a plain string. */
const code = computed<string[]>({
  get: () => Array.from({ length: props.length }, (_, index) => props.modelValue.charAt(index)),
  set: (slots) => {
    const filled = Array.from(slots.slice(0, props.length), (slot) => String(slot ?? ''))
    const firstGapIndex = filled.indexOf('')
    const contiguous = firstGapIndex === -1 ? filled : filled.slice(0, firstGapIndex)
    emit('update:modelValue', contiguous.join('').replace(ACCEPTED_CHARACTERS[props.mode], ''))
  }
})

const onComplete = (slots: readonly (string | number)[]): void => {
  emit('complete', slots.join(''))
}

const pinInput = useTemplateRef<{ inputsRef?: { $el?: HTMLElement }[] }>('pinInput')

defineExpose({
  focus: () => pinInput.value?.inputsRef?.[0]?.$el?.focus()
})
</script>
